/**
 * Dashboard header component with company name and property usage progress bar.
 * Matches mobile CompanyHeader design with purple gradient background.
 */

import { useTranslation } from 'react-i18next';

interface DashboardHeaderProps {
  companyName: string;
  logoUrl?: string | null;
  totalListings: number;
  maxListings: number;
  usagePercent: number;
  totalMembers?: number;
  maxMembers?: number;
  hasSubscription?: boolean;
}

export function DashboardHeader({
  companyName,
  logoUrl,
  totalListings,
  maxListings,
  usagePercent,
  totalMembers,
  maxMembers,
  hasSubscription,
}: DashboardHeaderProps) {
  const { t } = useTranslation('dashboard');

  // Ensure percent is between 0 and 100
  const clampedPercent = Math.min(100, Math.max(0, usagePercent));

  return (
    <div className="rounded-2xl border border-[var(--pv-border-soft)] bg-white p-5 shadow-brand-xs">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--pv-color-text)]">{companyName}</h1>
          <p className="mt-1 text-[var(--pv-color-text-muted)]">{t('company.title')}</p>
        </div>
        {/* Company logo or placeholder */}
        <div className="flex-shrink-0">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName}
              className="h-12 w-12 rounded-lg object-cover bg-white/10"
            />
          ) : (
            <div className="text-[#B7DDD7]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Property Usage Section */}
      <div className="mt-5">
        <div className="flex items-center justify-between">
          <span className="text-sm text-[var(--pv-color-text-muted)]">{t('company.propertyUsage')}</span>
          <span className="text-sm font-semibold text-[var(--pv-color-text)]">
            {totalListings} / {maxListings}
          </span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#DDEEEB]">
          <div
            className="h-full bg-[#004D40] transition-all duration-300"
            style={{ width: `${clampedPercent}%` }}
          />
        </div>
      </div>

      {/* Team Member Usage Section */}
      {maxMembers !== undefined && (
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--pv-color-text-muted)]">{t('company.teamUsage', 'Team Members')}</span>
            <span className="text-sm font-semibold text-[var(--pv-color-text)]">
              {totalMembers || 0} / {maxMembers}
              {!hasSubscription && (
                <span className="text-xs text-[var(--pv-color-text-subtle)] ml-1">(free)</span>
              )}
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#DDEEEB]">
            <div
              className="h-full bg-[#004D40] transition-all duration-300"
              style={{ width: `${Math.min(100, ((totalMembers || 0) / Math.max(1, maxMembers)) * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
